/**
 * Entropy GraphQL API
 *
 * Thin fetch wrapper for Entropy mutations called from CAP.
 * Auth: Supabase Bearer JWT (user token — service callers are blocked by Entropy).
 */
import { ENTROPY_URL } from './environmentUtils';
import { logError } from './logError';

import type { LineOfBusiness } from './constants';

const SYNC_USER_PROFILE_MUTATION = `
  mutation SyncUserProfile(
    $firstName: String!
    $lastName: String!
    $dateOfBirth: String!
    $localTimezone: String!
    $lob: LineOfBusiness!
  ) {
    syncUserProfile(
      firstName: $firstName
      lastName: $lastName
      dateOfBirth: $dateOfBirth
      localTimezone: $localTimezone
      lob: $lob
    ) {
      success
    }
  }
`;

interface SyncUserProfileArgs {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  localTimezone: string;
  lob: LineOfBusiness;
}

/**
 * Fire-and-forget: enqueues a PostHog identify event via Entropy.
 * Errors are logged but never surface to the user — a sync failure
 * must not block or break the sign-up flow.
 */
export const syncUserProfile = async (
  accessToken: string,
  args: SyncUserProfileArgs,
): Promise<void> => {
  try {
    const response = await fetch(`${ENTROPY_URL}/api/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        query: SYNC_USER_PROFILE_MUTATION,
        variables: args,
      }),
    });

    if (!response.ok) {
      logError('[syncUserProfile]', `failed with status: ${response.status}`, undefined, { status: response.status });
      return;
    }

    const data = await response.json();
    if (data.errors) {
      logError('[syncUserProfile]', 'GraphQL errors returned', undefined, { errors: data.errors });
    }
  } catch (err) {
    logError('[syncUserProfile]', 'network error', err);
  }
};
